import { fn } from '@ember/helper';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import MarketCustomReportingFormFieldsBreadcrumbs from 'lumen/components/market/custom-reporting/form/fields/breadcrumbs';
import type { LookerDashboard } from 'lumen/models';

type Changeset = Pick<LookerDashboard, 'title'>;

interface MarketCustomReportingFormDashboardSignature {
  Args: {
    afterSave: (dashboard: LookerDashboard) => void;
    close: () => void;
    dashboard: LookerDashboard;
  };
}

export default class MarketCustomReportingFormDashboard extends Component<MarketCustomReportingFormDashboardSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];

  changeset = trackedObject<Changeset>({
    title: this.args.dashboard.title,
  });

  saveTask = task({ drop: true }, async (): Promise<void> => {
    const { dashboard, close, afterSave } = this.args;
    const { title } = this.changeset;

    try {
      dashboard.title = title;

      await dashboard.save();
      afterSave(dashboard);
    } catch (e) {
      dashboard.rollbackAttributes();

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert();
      this.errorTracking.captureException(e);
    }

    close();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasTitleConflict() {
    const { title } = this.changeset;
    const { dashboard } = this.args;

    if (title === dashboard.title) {
      return false;
    }

    const dashboards = dashboard.folder.dashboards.map((d) =>
      d.title?.toLocaleLowerCase(),
    );
    return dashboards.includes(title.toLocaleLowerCase());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSaveDisabled() {
    const { title } = this.changeset;

    return (
      !title || this.hasTitleConflict || title === this.args.dashboard.title
    );
  }

  <template>
    <PrismModal
      @onClose={{@close}}
      @title={{if
        @dashboard.isNew
        (t "custom_reporting.new_dashboard")
        @dashboard.title
      }}
      class="custom-reporting__modal"
      data-test-id="dashboard-modal"
    >
      <:default>
        <div class="modal--padded custom-reporting-form">
          <MarketCustomReportingFormFieldsBreadcrumbs
            @folder={{@dashboard.folder}}
          />
          <div class="flex w-full flex-row">
            <PrismInput
              @autofocus={{true}}
              @errorMessage={{t
                "custom_reporting.form.dashboard_title_conflict"
              }}
              @hasErrors={{this.hasTitleConflict}}
              @isTouched={{true}}
              @label={{t "custom_reporting.form.title"}}
              @onInput={{pick "target.value" (fn (mut this.changeset.title))}}
              @placeholder={{t "custom_reporting.form.title_placeholder"}}
              @required={{true}}
              @value={{this.changeset.title}}
              data-test-id="dashboard-title-input"
            />
          </div>
        </div>
      </:default>
      <:footer>
        <div class="flex h-full items-center justify-end gap-300 pr-500 pl-500">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@close}}
            data-test-id="cancel-button"
          />
          <PrismButton
            @isDisabled={{or this.isSaveDisabled this.saveTask.isRunning}}
            @isLoading={{this.saveTask.isRunning}}
            @label={{if
              @dashboard.isNew
              (t "general.create")
              (t "general.save")
            }}
            @onClick={{this.saveTask.perform}}
            data-test-id="save-button"
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
